import React, { useEffect } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import { getState, history } from "../../store/configure/configureStore";
import { Role } from "../../constants/role";

import RestrictedRoute from "../restricted/index";
import LoginContainer from "./Login/LoginContainer";
import ForgotPasswordContainer from "./ForgotPassword/ForgotPasswordContainer";
import ResetPasswordContainer from "./ResetPassword/ResetPasswordContainer";
import ChangePasswordContainer from "./ChangePassword/ChangePasswordContainer";
import MobileNoForOtpContainer from "./OtpVerification/MobileNoForOtp/MobileNoForOtpContainer";
import VerifyOtpContainer from "./OtpVerification/VerifyOtp/VerifyOtpConatiner";
//Routes folder imports
import TestScreenContainer from "./Routes/TestScreen/TestScreenContainer";
import HomeContainer from "./Routes/Home/HomeContainer";
import UserManagementContainer from "./Routes/UserManagement/UserManagementContainer";
//
import MobileAppUserManagementContainer from "./Routes/MobileAppUserManagement/MobileAppUserManagementContainer";
import MobileUserDetailsContainer from "./Routes/MobileUserDetails/MobileUserDetailsContainer";
//
import SendNotificationContainer from "./Routes/SendNotification/SendNotificationContainer";
//
import NotificationManagementContainer from "./Routes/NotificationManagement/NotificationManagementContainer";
import AddNewNotificationContainer from "./Routes/AddNewNotification/AddNewNotificationContainer";
import UpdateNotificationContainer from "./Routes/UpdateNotification/UpdateNotificationContainer";
//
import YouTubeManagementContainer from "./Routes/YouTubeManagement/YouTubeManagementContainer";
import InstagramManagementContainer from "./Routes/InstagramManagement/InstagramManagementContainer";
import BannerManagementContainer from "./Routes/BannerManagement/BannerManagementContainer";
import AddBannerContainer from "./Routes/AddBanner/AddBannerContainer";
import UpdateBannerContainer from "./Routes/UpdateBanner/UpdateBannerContainer";
import ViewBannerContainer from "./Routes/ViewBanner/ViewBannerContainer";
import ContentDetailsContainer from "./Routes/ContentDetail/ContentDetailsContainer";
import ContentDetailsInstagramContainer from "./Routes/ContentDetailInstagram/ContentDetailsInstagramContainer";
import TransactionHistoryContainer from "./Routes/TransactionHistory/TransactionHistoryContainer";

import NotificationActivityLogContainer from "./Routes/NotificationActivityLogs/NotificationActivityLogContainer";
import MobileUsersActivityLogContainer from "./Routes/MobileUsersActivityLogs/MobileUsersActivityLogContainer";
import MobileUserActivityDetailsContainer from "./Routes/MobileUserActivityLogDetails/MobileUserActivityDetailsContainer";

import PageNotFound from "../common/PageNotFound/PageNotFound";

import { APP_VERSION } from "../../constants/constants";

function App({ match }) {
  // console.log("match:", match);

  useEffect(() => {
    //this code block is to reflect the changes on deployment
    const currentVersion = APP_VERSION; // The current version of app
    const savedVersion = localStorage.getItem("appVersion"); // Get the saved version from localStorage

    if (savedVersion !== currentVersion) {
      // If the version has changed, store the new version in localStorage and reload
      const persistedState = localStorage.getItem("persist:root");

      if (persistedState) {
        const parsedState = JSON.parse(persistedState);

        // Keep only the 'auth' state, remove others
        const updatedState = {
          auth: parsedState?.auth || {}, // Retain the auth state, if it exists
        };

        // Save the updated state back to localStorage
        localStorage.setItem("persist:root", JSON.stringify(updatedState));
      }
      localStorage.setItem("appVersion", currentVersion);
      window.location.reload(); // Hard reload the page to fetch the new app version
    }
  }, []);

  const loggedInUserRole = getState().auth?.userRole; //get role from redux
  const userData = getState().auth?.loginUser; //get role from redux

  //redirection for admin
  var defaultRedirectionForAdmin = (
    <Redirect
      exact
      from={`${match.url}`}
      to={`${match.url}user-management/admin-panel-users`}
    />
  );

  //redirection for reviewer, editor and viewer
  var defaultRedirectionForOtherUsers = (
    <Redirect
      exact
      from={`${match.url}`}
      // to={`${match.url}media/youtube-management`}
      to={`${match.url}media/notification-management`}
    />
  );

  //redirect based on role
  const defaultRedirection =
    loggedInUserRole === Role?.Administrator
      ? defaultRedirectionForAdmin
      : defaultRedirectionForOtherUsers;

  const admin = Role?.Administrator;
  const editor = Role?.Editor;
  const reviewer = Role?.Moderator;
  const viewer = Role?.Viewer;

  var routes = [
    {
      component: HomeContainer,
      link: "home",
      permissions: [admin, editor, reviewer, viewer],
    },
    {
      component: UserManagementContainer,
      link: "user-management/admin-panel-users",
      permissions: [admin],
    },
    {
      component: MobileAppUserManagementContainer,
      link: "user-management/mobile-app-users",
      permissions: [admin],
      isExact: true,
    },
    {
      component: MobileUsersActivityLogContainer,
      link: "user-management/mobile-app-users/user-activity-log/:id",
      permissions: [admin, editor],
      isExact: true,
    },
    {
      component: MobileUserActivityDetailsContainer,
      link: "user-management/mobile-app-users/user-activity-log/log-details/:id",
      permissions: [admin, editor],
      isExact: true,
    },
    {
      component: MobileUserDetailsContainer,
      link: "mobile-app-users/user-details/:id",
      permissions: [admin],
    },
    {
      component: YouTubeManagementContainer,
      link: "media/youtube-management",
      permissions: [admin, editor, reviewer, viewer],
    },
    {
      component: InstagramManagementContainer,
      link: "media/instagram-management",
      permissions: [admin, editor, reviewer, viewer],
    },
    {
      component: BannerManagementContainer,
      link: "media/banner-management",
      permissions: [admin, editor, reviewer],
    },
    {
      component: AddBannerContainer,
      link: "media/add-banner",
      permissions: [admin, editor, reviewer],
    },
    {
      component: UpdateBannerContainer,
      link: "media/edit-banner/:id",
      permissions: [admin, editor, reviewer],
    },
    {
      component: ViewBannerContainer,
      link: "media/view-banner/:id",
      permissions: [admin, editor, reviewer],
    },
    {
      component: NotificationManagementContainer,
      link: "media/notification-management",
      permissions: [admin, editor, viewer],
      isExact: true,
    },
    {
      component: AddNewNotificationContainer,
      link: "media/notification-management/add-notification",
      permissions: [admin, editor],
    },
    {
      component: UpdateNotificationContainer,
      link: "media/notification-management/edit-notification/:id",
      permissions: [admin, editor],
    },
    {
      component: NotificationActivityLogContainer,
      link: "media/notification-management/activity-log/:id",
      permissions: [admin, editor],
    },
    {
      component: SendNotificationContainer,
      link: "media/notification-management/send-notifications/:id",
      permissions: [admin, editor],
    },
    {
      component: ContentDetailsContainer,
      link: "media/content-detail/youtube/:id",
      permissions: [admin, editor, reviewer, viewer],
    },
    {
      component: ContentDetailsInstagramContainer,
      link: "media/content-detail/instagram",
      permissions: [admin, editor, reviewer, viewer],
    },
    {
      component: TransactionHistoryContainer,
      link: "subscription/transaction-history",
      permissions: [admin],
    },
    {
      component: TestScreenContainer,
      link: "test-screen",
      permissions: [admin, editor, reviewer, viewer],
    },
    {
      component: ChangePasswordContainer,
      link: "change_password",
      permissions: [admin, editor, reviewer, viewer],
    },
  ];

  var login_routes = [
    {
      component: LoginContainer,
      link: "/login",
    },
    {
      component: ForgotPasswordContainer,
      link: "/forgot_password",
    },
    {
      component: ResetPasswordContainer,
      link: "/reset_password",
    },
    {
      component: MobileNoForOtpContainer,
      link: "/otpverification",
    },
    {
      component: VerifyOtpContainer,
      link: "/verifyotp",
    },
  ];

  //show error page on invalid path
  if (loggedInUserRole) {
    routes.push({
      component: PageNotFound,
      link: "*",
      permissions: [admin, editor, reviewer, viewer],
    });
  } else {
    login_routes.push({
      component: PageNotFound,
      link: "*",
      permissions: [admin, editor, reviewer, viewer],
    });
  }

  const redirectToPreviousRouteOnInvalidPath = () => {
    const currentPath = window.location.pathname;

    // Check if the current path is part of login routes
    const isLoginRoute = login_routes.some((item) => currentPath === item.link);

    if (isLoginRoute) {
      // If the current path is a login route, do nothing
      return;
    }

    // Check if the current path matches any route's base path, even with parameters
    const isPathValid = routes.some((item) => {
      const routeBasePath = item.link.split("/:")[0]; // Extract base path, ignoring dynamic segments
      const baseCurrentPath = currentPath.split("?")[0]; // Remove query params from current path
      return baseCurrentPath.startsWith(`/${routeBasePath}`);
    });

    // If the path is invalid, go back
    if (!isPathValid) {
      history.goBack();
    }
  };

  return (
    <Switch>
      {defaultRedirection}

      {/* {redirectToPreviousRouteOnInvalidPath()} */}

      {routes.map((element) => (
        <RestrictedRoute
          path={`${match.url}${element.link}`}
          component={element.component}
          permissions={element?.permissions}
          exact={element?.isExact}
        />
      ))}

      {login_routes.map((element) => (
        <Route exact path={element.link} component={element.component} />
      ))}
    </Switch>
  );
}

export default App;
